import { useCallback, useEffect, useState } from "react";
import { fetchHealth, fetchProviders, fetchTickers } from "../lib/api";

/**
 * Loads the dashboard's read-once payload (tickers + providers + health)
 * in parallel. Exposes a `refresh(force)` function that re-runs the same
 * pipeline with `?refresh=true` semantics for the tickers endpoint.
 *
 * @returns {{
 *   tickers: Array|null,
 *   tickersError: Error|null,
 *   loading: boolean,
 *   refreshing: boolean,
 *   lastUpdated: number|null,
 *   providers: Object|null,
 *   health: Object|null,
 *   refresh: (force?: boolean) => Promise<void>
 * }}
 */
export function useDashboardData() {
  const [tickers, setTickers] = useState(null);
  const [tickersError, setTickersError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [providers, setProviders] = useState(null);
  const [health, setHealth] = useState(null);

  const refresh = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setTickersError(null);
    try {
      const [t, p, h] = await Promise.all([
        fetchTickers({ refresh: force }),
        fetchProviders(),
        fetchHealth(),
      ]);
      setTickers(t.tickers);
      setProviders(p);
      setHealth(h);
      setLastUpdated(Date.now());
    } catch (e) {
      setTickersError(e);
    } finally {
      if (force) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  return {
    tickers,
    tickersError,
    loading,
    refreshing,
    lastUpdated,
    providers,
    health,
    refresh,
  };
}
